import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { useStudioStore } from '@/store/useStudioStore';
import { projectApi } from '@/api/project';
import { WebsiteVersionRecord, WebpageDraft } from '@/types/project';
import { buildDraftPreviewHtml } from '@/lib/domUtils';
import { resolveEditableDraft } from '@/lib/draftUtils';
import { isSpecialProjectType } from '@/lib/projectUtils.ts';
import { loadSpecialProjectPreviewHtml } from '@/lib/specialProjectPreview';
import { trackStudioEvent } from '@/lib/studioTracking';
import { getTrackingErrorCode } from '@/lib/trackingUtils';

interface StudioVersionHistoryProps {
  isOpen: boolean;
  onClose?: () => void;
}

const formatTime = (createTime: string) => {
  const d = new Date(createTime);
  return (
    d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) +
    ', ' +
    d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
  );
};

const normalizeVersionToken = (value?: string | number | null) => {
  const text = String(value ?? '')
    .trim()
    .toLowerCase();
  if (!text) return null;
  return text.startsWith('v') ? text.slice(1) : text;
};

const getRollbackTargetVersionToken = (changeNote?: string | null) => {
  const text = String(changeNote ?? '').trim();
  if (!text) return null;

  const rollbackFromToMatch = text.match(/从\s*(v?[\d.]+)\s*回滚到\s*(v?[\d.]+)/i);
  if (rollbackFromToMatch) {
    return normalizeVersionToken(rollbackFromToMatch[2]);
  }

  const rollbackToMatch = text.match(/回滚到\s*(v?[\d.]+)/i);
  if (rollbackToMatch) {
    return normalizeVersionToken(rollbackToMatch[1]);
  }

  const rollbackFromMatch = text.match(/从\s*(v?[\d.]+)\s*回滚/i);
  if (rollbackFromMatch) {
    return normalizeVersionToken(rollbackFromMatch[1]);
  }

  const restoredToMatch = text.match(/restored to\s*(v?[\d.]+)/i);
  if (restoredToMatch) {
    return normalizeVersionToken(restoredToMatch[1]);
  }

  const rolledBackFromToMatch = text.match(/rolled back from\s*(v?[\d.]+)\s*to\s*(v?[\d.]+)/i);
  if (rolledBackFromToMatch) {
    return normalizeVersionToken(rolledBackFromToMatch[2]);
  }

  const rolledBackFromMatch = text.match(/rolled back from\s*(v?[\d.]+)/i);
  if (rolledBackFromMatch) {
    return normalizeVersionToken(rolledBackFromMatch[1]);
  }

  return null;
};

const getDraftTimestamp = (draft?: WebpageDraft | null) => {
  if (!draft?.modifyTime) return 0;
  const timestamp = new Date(draft.modifyTime).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

const getNewerDraft = (left?: WebpageDraft | null, right?: WebpageDraft | null) => {
  if (!left) return right ?? null;
  if (!right) return left;

  if (left.version !== right.version) {
    return left.version > right.version ? left : right;
  }

  return getDraftTimestamp(left) >= getDraftTimestamp(right) ? left : right;
};

export const StudioVersionHistory = ({ isOpen }: StudioVersionHistoryProps) => {
  const pendingReviewTooltip = 'Accept or keep the latest change first.';
  const currentProjectId = useStudioStore((state) => state.currentProjectId);
  const currentId = useStudioStore((state) => state.currentId);
  const currentProjectTypeId = useStudioStore((state) => state.currentProjectTypeId);
  const isPreviewReviewActive = useStudioStore((state) => state.previewReview.active);
  const setGeneratedHtml = useStudioStore((state) => state.setGeneratedHtml);
  const setIframeUrl = useStudioStore((state) => state.setIframeUrl);
  const setCurrentDraftMeta = useStudioStore((state) => state.setCurrentDraftMeta);
  const startPreviewReview = useStudioStore((state) => state.startPreviewReview);
  const clearPreviewReview = useStudioStore((state) => state.clearPreviewReview);
  const isAiWebpage = !isSpecialProjectType(currentProjectTypeId);

  const [versions, setVersions] = useState<WebsiteVersionRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [restoreTarget, setRestoreTarget] = useState<WebsiteVersionRecord | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [currentVersionId, setCurrentVersionId] = useState<number | null>(null);

  useEffect(() => {
    if (!isPreviewReviewActive) return;
    setRestoreTarget(null);
  }, [isPreviewReviewActive]);

  const resolveCurrentVersionId = useCallback(
    async (versionList: WebsiteVersionRecord[]) => {
      const fallbackVersionId = versionList[0]?.id ?? null;
      if (!isAiWebpage || !currentId || versionList.length === 0) {
        return fallbackVersionId;
      }

      try {
        const [pendingDraft, confirmedDraft] = await Promise.all([
          projectApi.getLatestDraft(Number(currentId), ['PENDING']),
          projectApi.getLatestDraft(Number(currentId), ['CONFIRMED']),
        ]);
        const pendingRollbackTarget = getRollbackTargetVersionToken(pendingDraft?.changeNote);
        const confirmedRollbackTarget = getRollbackTargetVersionToken(confirmedDraft?.changeNote);
        const latestRollbackDraft = getNewerDraft(
          pendingRollbackTarget ? pendingDraft : null,
          confirmedRollbackTarget ? confirmedDraft : null,
        );
        const rollbackTargetVersion =
          getRollbackTargetVersionToken(latestRollbackDraft?.changeNote) ??
          pendingRollbackTarget ??
          confirmedRollbackTarget;
        if (!rollbackTargetVersion) {
          return fallbackVersionId;
        }

        const matchedVersion = versionList.find((version) => {
          const semanticVersion = normalizeVersionToken(version.semanticVersion);
          const versionNumber = normalizeVersionToken(version.versionNumber);
          return (
            semanticVersion === rollbackTargetVersion || versionNumber === rollbackTargetVersion
          );
        });

        return matchedVersion?.id ?? fallbackVersionId;
      } catch {
        return fallbackVersionId;
      }
    },
    [currentId, isAiWebpage],
  );

  useEffect(() => {
    if (!isOpen) return;

    const id = isAiWebpage ? currentId : currentProjectId;
    if (!id) return;

    let cancelled = false;
    setLoading(true);

    const params = isAiWebpage
      ? { userProjectId: Number(currentId) }
      : { projectId: Number(currentProjectId) };

    void projectApi
      .getProjectVersions(params)
      .then(async (data) => {
        if (cancelled) return;
        setVersions(data);
        const nextCurrentVersionId = await resolveCurrentVersionId(data);
        if (!cancelled) {
          setCurrentVersionId(nextCurrentVersionId);
        }
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Failed to load version history');
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentId, currentProjectId, isAiWebpage, isOpen, resolveCurrentVersionId]);

  const openPendingDraftReview = (
    pendingReview: Awaited<ReturnType<typeof resolveEditableDraft>>['pendingReview'],
  ) => {
    if (!pendingReview) return;

    clearPreviewReview();
    setGeneratedHtml(pendingReview.beforeHtml);
    setCurrentDraftMeta(
      pendingReview.currentDraft.id,
      pendingReview.currentDraft.version,
      pendingReview.currentDraft.draftStatus,
    );
    startPreviewReview({
      draftId: pendingReview.pendingDraft.id,
      version: pendingReview.pendingDraft.version,
      beforeHtml: pendingReview.beforeHtml,
      afterHtml: pendingReview.afterHtml,
      title: 'Review restored version',
      note: 'Compare the current draft with the restored version, then accept the restored draft or keep the current one.',
      sourceMessageId: null,
    });
  };

  const handleRestoreClick = (version: WebsiteVersionRecord) => {
    if (isPreviewReviewActive) {
      toast.error('Finish the current draft review before restoring a version.');
      return;
    }

    setRestoreTarget(version);
  };

  const handleConfirmRestore = async () => {
    if (!restoreTarget) return;
    if (isPreviewReviewActive) {
      toast.error('Finish the current draft review before restoring a version.');
      return;
    }

    const id = isAiWebpage ? currentId : currentProjectId;
    if (!id) return;

    setConfirming(true);
    try {
      const rollbackParams = isAiWebpage
        ? { userProjectId: Number(currentId), releaseId: restoreTarget.id }
        : { projectId: Number(currentProjectId), versionId: restoreTarget.id };
      const result = await projectApi.rollbackProjectVersion(rollbackParams);

      toast.success(
        `Restored to ${restoreTarget.semanticVersion ? `v${restoreTarget.semanticVersion}` : `v${restoreTarget.versionNumber}`}`,
      );

      const versionsParams = isAiWebpage
        ? { userProjectId: Number(currentId) }
        : { projectId: Number(currentProjectId) };
      const updated = await projectApi.getProjectVersions(versionsParams);
      setVersions(updated);
      setCurrentVersionId(await resolveCurrentVersionId(updated));

      if (isAiWebpage) {
        const { editableDraft, pendingReview } = await resolveEditableDraft(Number(currentId));
        if (pendingReview) {
          openPendingDraftReview(pendingReview);
        } else if (editableDraft?.content) {
          clearPreviewReview();
          setGeneratedHtml(buildDraftPreviewHtml(editableDraft.content));
          setCurrentDraftMeta(editableDraft.id, editableDraft.version, editableDraft.draftStatus);
        }
      } else {
        const localPreviewHtml = await loadSpecialProjectPreviewHtml(currentProjectId);
        if (localPreviewHtml) {
          setGeneratedHtml(buildDraftPreviewHtml(localPreviewHtml));
          setIframeUrl(null);
        } else if (result?.previewUrl) {
          setGeneratedHtml('');
          setIframeUrl(result.previewUrl);
        }
      }

      trackStudioEvent('studio_version_restore_result', {
        result: 'success',
        targetVersion: restoreTarget.semanticVersion ?? restoreTarget.versionNumber,
        targetDraftId: isAiWebpage ? restoreTarget.id : undefined,
        source: isAiWebpage ? 'draft' : 'version',
      });
      setRestoreTarget(null);
    } catch (err: unknown) {
      trackStudioEvent('studio_version_restore_result', {
        result: 'failure',
        targetVersion: restoreTarget.semanticVersion ?? restoreTarget.versionNumber,
        targetDraftId: isAiWebpage ? restoreTarget.id : undefined,
        source: isAiWebpage ? 'draft' : 'version',
        errorCode: getTrackingErrorCode(err),
      });
      toast.error((err as { message?: string })?.message || 'Restore failed. Please try again.');
    } finally {
      setConfirming(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="animate-in fade-in zoom-in-95 border-border absolute top-[calc(100%+8px)] right-0 z-[150] w-[296px] origin-top-right overflow-hidden rounded-2xl border bg-white shadow-[0_12px_36px_rgba(0,0,0,0.13),0_2px_8px_rgba(0,0,0,0.06)] duration-200">
        <div className="border-border flex items-center justify-between border-b px-4 py-3">
          <span className="text-[13px] font-bold text-[#0D0D1A]">Version History</span>
          {!loading && (
            <span className="text-[11px] font-semibold text-[#0D0D1A]/40">
              {versions.length} versions
            </span>
          )}
        </div>

        {isPreviewReviewActive && (
          <div className="border-b border-[rgba(217,119,6,0.18)] bg-[rgba(217,119,6,0.08)] px-4 py-[10px] text-[11.5px] leading-[1.5] text-[#92400E]">
            Finish the current draft review before using restore.
          </div>
        )}

        <div className="max-h-[280px] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-8 text-[13px] text-black/40">
              <Loader2 size={16} className="mr-2 animate-spin" />
              Loading...
            </div>
          ) : versions.length === 0 ? (
            <div className="py-8 text-center text-[13px] text-black/40">No versions yet</div>
          ) : (
            versions.map((version, index) => {
              const isLive =
                currentVersionId !== null ? version.id === currentVersionId : index === 0;
              const rawLabel = version.semanticVersion
                ? `v${version.semanticVersion}`
                : `v${version.versionNumber}`;

              return (
                <div
                  key={version.id}
                  className={`border-border group flex items-center justify-between gap-[10px] border-b px-4 py-[10px] transition-colors last:border-none hover:bg-black/[0.022] ${
                    isLive ? 'bg-[rgba(15,23,42,0.04)]' : ''
                  }`}
                >
                  <div className="min-w-0 flex-1">
                    <div className="text-[13px] font-semibold text-[#0D0D1A]">
                      {isLive ? (
                        <>
                          <span>{rawLabel}</span>
                          <span className="mx-1">&mdash;</span>
                          <span>Current</span>
                        </>
                      ) : (
                        rawLabel
                      )}
                    </div>
                    <div className="mt-[2px] text-[11px] text-[#0D0D1A]/40">
                      {formatTime(version.createTime)}
                    </div>
                  </div>

                  <div className="group/restore relative shrink-0">
                    <button
                      className="pointer-events-none shrink-0 translate-x-[6px] cursor-pointer rounded-[7px] border border-[rgba(109,40,217,0.25)] bg-transparent px-[10px] py-[4px] text-[12px] font-bold whitespace-nowrap text-[#6D28D9] opacity-0 transition-all group-hover:pointer-events-auto group-hover:translate-x-0 group-hover:opacity-100 hover:border-[rgba(109,40,217,0.45)] hover:bg-[rgba(109,40,217,0.07)] focus-visible:pointer-events-auto focus-visible:translate-x-0 focus-visible:opacity-100 disabled:pointer-events-auto disabled:translate-x-0 disabled:cursor-default disabled:border-[rgba(148,163,184,0.22)] disabled:bg-[rgba(148,163,184,0.08)] disabled:text-[rgba(15,23,42,0.38)] disabled:opacity-100"
                      onClick={() => handleRestoreClick(version)}
                      disabled={isPreviewReviewActive}
                    >
                      Restore
                    </button>
                    {isPreviewReviewActive && (
                      <div className="pointer-events-none absolute top-[calc(100%+8px)] right-0 z-[170] w-max max-w-[220px] rounded-[10px] bg-[#0D0D1A] px-[10px] py-[7px] text-[11.5px] leading-[1.45] font-medium whitespace-normal text-white opacity-0 shadow-[0_12px_28px_rgba(15,23,42,0.22)] transition-opacity duration-150 group-hover/restore:opacity-100">
                        {pendingReviewTooltip}
                      </div>
                    )}
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      {restoreTarget && (
        <div className="fixed inset-0 z-[500] flex items-center justify-center bg-black/40 p-5 backdrop-blur-sm">
          <div className="animate-in fade-in zoom-in-95 w-[min(420px,100%)] overflow-hidden rounded-2xl bg-white shadow-[0_24px_56px_rgba(0,0,0,0.18)] duration-200">
            <div className="border-border border-b px-5 pt-[18px] pb-[14px]">
              <div className="mb-1 text-[15px] font-[800] tracking-[-0.3px] text-[#0D0D1A]">
                Restore version?
              </div>
              <div className="text-[12.5px] leading-[1.6] text-[#0D0D1A]/56">
                Restoring will publish this version as the latest live page.
              </div>
            </div>

            <div className="bg-[rgba(247,247,252,0.7)] px-5 py-[14px] text-[12.5px] leading-[1.6] text-[#0D0D1A]/56">
              You are about to restore
              <strong className="font-bold text-[#0D0D1A]">
                {restoreTarget.semanticVersion
                  ? `v${restoreTarget.semanticVersion}`
                  : `v${restoreTarget.versionNumber}`}
              </strong>
              .
              <div className="mt-[10px] flex items-start gap-2 rounded-[9px] border border-[rgba(217,119,6,0.22)] bg-[rgba(217,119,6,0.07)] px-3 py-[10px] text-[12px] text-[#92400E]">
                <i className="fa fa-triangle-exclamation mt-[1px] shrink-0 text-amber-500" />
                <span>
                  Any <strong className="font-bold text-[#92400E]">unpublished changes</strong> in
                  the current draft will be discarded. This will automatically publish the restored
                  version.
                </span>
              </div>
            </div>

            <div className="border-border flex justify-end gap-[9px] border-t px-5 pt-[14px] pb-[18px]">
              <button
                className="border-border cursor-pointer rounded-[9px] border bg-white px-[18px] py-[9px] text-[13px] font-bold text-[#0D0D1A]/56 transition-all hover:bg-[#F7F7FC]"
                onClick={() => setRestoreTarget(null)}
                disabled={confirming}
              >
                Cancel
              </button>
              <button
                className="cursor-pointer rounded-[9px] border-none bg-[linear-gradient(135deg,#6D28D9_0%,#4F46E5_100%)] px-5 py-[9px] text-[13px] font-bold text-white transition-opacity hover:opacity-90 disabled:opacity-60"
                onClick={handleConfirmRestore}
                disabled={confirming || isPreviewReviewActive}
              >
                {confirming ? (
                  <span className="flex items-center gap-[6px]">
                    <Loader2 size={13} className="animate-spin" /> Restoring...
                  </span>
                ) : (
                  <span className="flex items-center gap-[6px]">
                    <i className="fa fa-clock-rotate-left" /> Restore
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};
